import {
  AccountBalance,
  Assignment,
  AssignmentInd,
  AttachMoney,
  BusinessCenter,
  Description,
  MoreHoriz,
  OpenInNew,
  Person,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { SGR_STATES } from 'lib/consts';
import { formatCurrency } from 'lib/functions/utils';
import React from 'react';

type Menu = {
  onOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseMenu: () => void;
  menuAnchorEl: HTMLElement | null;
  menuOpen: boolean;
  menuOptions: { label: string; action: () => void }[];
};

interface GeneralInfoProps {
  warrantyStatus: (typeof SGR_STATES)[number] | undefined;
  warrantyType: string;
  quotedAmount?: number | null;
  quotedCurrency?: 'CLP' | 'USD' | 'UF' | null;
  tenderId?: string | number | null;
  description?: string | null;
  business?: { primary?: string; secondary?: string };
  tenderUrl?: string;
  menu?: Menu;
}

const GeneralInfo: React.FC<GeneralInfoProps> = ({
  warrantyStatus,
  warrantyType,
  quotedAmount,
  quotedCurrency,
  tenderId,
  description,
  business,
  tenderUrl,
  menu,
}) => {
  return (
    <Paper
      sx={{
        p: 1.5,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        mb: 2,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.1,
          }}
        >
          Información General
        </Typography>
        <Chip
          label={warrantyStatus?.label}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: '18px', fontSize: '0.65rem' }}
        />
        {menu && (
          <>
            <IconButton
              aria-label="Opciones"
              size="small"
              sx={{ position: 'absolute', right: 8, top: 8 }}
              onClick={menu.onOpenMenu}
            >
              <MoreHoriz />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={menu.menuAnchorEl}
              open={menu.menuOpen}
              onClose={menu.onCloseMenu}
              MenuListProps={{ 'aria-labelledby': 'long-button' }}
            >
              {menu.menuOptions.map((opt) => (
                <MenuItem
                  key={opt.label}
                  onClick={() => {
                    menu.onCloseMenu();
                    opt.action();
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BusinessCenter
                sx={{ fontSize: '0.8rem', color: 'primary.main' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Tipo de Garantía
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {warrantyType}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AttachMoney sx={{ fontSize: '0.8rem', color: 'success.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Monto Cotizado
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {formatCurrency(quotedAmount ?? 0, quotedCurrency)}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Assignment sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Número de Licitación
                  {tenderUrl && (
                    <IconButton
                      component="a"
                      href={tenderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      aria-label="Abrir licitación"
                      sx={{ p: 0.25 }}
                    >
                      <OpenInNew sx={{ fontSize: '0.75rem' }} />
                    </IconButton>
                  )}
                </Box>
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                #{tenderId}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(0, 150, 136, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountBalance sx={{ fontSize: '0.8rem', color: 'info.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Fogape
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                No / -
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Person sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Afianzado
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {business?.primary}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AssignmentInd
                sx={{ fontSize: '0.8rem', color: 'primary.main' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                RUT Afianzado
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {business?.secondary}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(121, 85, 72, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Description sx={{ fontSize: '0.8rem', color: 'brown' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Glosa
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {description}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default GeneralInfo;
